import {
  forwardRef,
  isValidElement,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Helmet } from "react-helmet-async";
import { alpha } from "@mui/material/styles";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  LinearProgress,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Slide,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField as MuiTextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useForm } from "react-hook-form";
import Loading from "../components/spinners/Loading";
import TextField from "../components/form/fields/TextField";
import SelectField from "../components/form/fields/SelectField";
import DEDateField from "../components/form/fields/DEDateField";
import BeltAvatar from "../components/beltavatar/BeltAvatar";
import { anredeArray, gradArray } from "../components/form/fields/inputFields";
import Iconify from "../components/iconify/Iconify";
import { getDateFromDateField } from "../utils/dataUtils";
import { getMemberStatusConfig } from "../utils/memberStatus";
import {
  useGetMemberSelfQuery,
  useUpdateMemberSelfMutation,
  useGetMemberSitePricingInfoQuery,
  useAddMemberToContractSelfMutation,
} from "../features/members/membersApiSlice";
import { serverURL } from "../app/api/axios/http.service";
import SelfTerminateMemberDialog from "../components/dialog/SelfTerminateMemberDialog";
import { selectIsPreviewActive } from "../features/rolePreview/rolePreviewSlice";
import { useSelector as useReduxSelector } from "react-redux";

// Only ISO date strings (YYYY-MM-DD…) are treated as dates — prevents PLZ / phone numbers being mis-parsed
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}(T|\s|Z|$)/;

const renderValue = (value) => {
  if (isValidElement(value)) return value;
  if (value === null || value === undefined || value === "") return "—";
  if (value instanceof Date) return value.toLocaleDateString("de-DE");
  if (typeof value === "string" && ISO_DATE_RE.test(value)) {
    const d = new Date(value);
    if (!Number.isNaN(d.getTime())) return d.toLocaleDateString("de-DE");
  }
  return String(value);
};

const MemberDialogTransition = forwardRef(function MemberDialogTransition(
  props,
  ref,
) {
  return <Slide direction="up" timeout={210} ref={ref} {...props} />;
});

const formatDateToInput = (value) => {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toISOString().slice(0, 10);
};

function StatusChip({ status }) {
  const cfg = getMemberStatusConfig(status);
  return (
    <Chip
      color={cfg.color}
      icon={<Iconify icon={cfg.icon} width={14} />}
      label={cfg.label}
      size="small"
      sx={{
        borderRadius: "8px",
        fontWeight: 600,
        px: 0.5,
        "& .MuiChip-label": { px: 0.75 },
      }}
    />
  );
}

function ReadOnlyBadge() {
  return (
    <Chip
      icon={<Iconify icon="mdi:lock-outline" width={13} />}
      label="Nur lesbar"
      size="small"
      sx={{
        height: 22,
        fontSize: "0.68rem",
        fontWeight: 600,
        borderRadius: "6px",
        bgcolor: "action.selected",
        color: "text.secondary",
        "& .MuiChip-label": { px: 0.75 },
        "& .MuiChip-icon": { color: "text.secondary", ml: 0.75 },
      }}
    />
  );
}

function KeyValueRow({ label, value }) {
  return (
    <Grid item xs={12} sm={6} md={4}>
      <Typography
        variant="caption"
        color="text.secondary"
        display="block"
        sx={{ mb: 0.25 }}
      >
        {label}
      </Typography>
      {isValidElement(value) ? (
        value
      ) : (
        <Typography variant="body2" fontWeight={500}>
          {renderValue(value)}
        </Typography>
      )}
    </Grid>
  );
}

function SectionAccordion({
  icon,
  title,
  badge,
  action,
  defaultExpanded = false,
  children,
}) {
  return (
    <Accordion
      defaultExpanded={defaultExpanded}
      disableGutters
      elevation={0}
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: "12px !important",
        "&:before": { display: "none" },
        "&.Mui-expanded": { borderColor: "primary.main" },
      }}
    >
      <AccordionSummary
        expandIcon={<Iconify icon="mdi:chevron-down" width={22} />}
        sx={{ px: { xs: 2, md: 3 }, py: 1.5, minHeight: 56 }}
      >
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          flex={1}
          mr={1}
        >
          <Iconify icon={icon} width={20} sx={{ color: "primary.main" }} />
          <Typography variant="subtitle1" fontWeight={600}>
            {title}
          </Typography>
          {badge}
        </Stack>
        {action && (
          <Box onClick={(e) => e.stopPropagation()} sx={{ mr: 1 }}>
            {action}
          </Box>
        )}
      </AccordionSummary>
      <AccordionDetails sx={{ px: { xs: 2, md: 3 }, pb: 3, pt: 0 }}>
        {children}
      </AccordionDetails>
    </Accordion>
  );
}

// Renders a labeled sub-group within the edit form
function FormSubSection({ icon, title, children }) {
  return (
    <Box>
      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 2 }}>
        <Iconify icon={icon} width={16} sx={{ color: "text.secondary" }} />
        <Typography
          variant="caption"
          fontWeight={700}
          color="text.secondary"
          sx={{ textTransform: "uppercase", letterSpacing: 0.8 }}
        >
          {title}
        </Typography>
      </Stack>
      {children}
    </Box>
  );
}

// Styled info/hint box used in read-only sections
function InfoHint({ icon = "mdi:information-outline", children }) {
  return (
    <Box
      sx={{
        mb: 2.5,
        p: 1.75,
        borderRadius: 2,
        bgcolor: (t) => alpha(t.palette.grey[500], 0.06),
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <Iconify
          icon={icon}
          width={18}
          sx={{ color: "text.secondary", mt: 0.2, flexShrink: 0 }}
        />
        <Typography variant="body2" color="text.secondary">
          {children}
        </Typography>
      </Stack>
    </Box>
  );
}

export default function MemberOverviewPage() {
  const {
    data: member,
    isLoading,
    isFetching,
    isError,
    error,
  } = useGetMemberSelfQuery();

  const [updateMemberSelf, updateState] = useUpdateMemberSelfMutation();
  const [addMemberToContractSelf, addMemberState] =
    useAddMemberToContractSelfMutation();
  const [terminateTarget, setTerminateTarget] = useState(null);
  const [contractActionFeedback, setContractActionFeedback] = useState(null);
  const [memberEditorOpen, setMemberEditorOpen] = useState(false);
  const [memberDraft, setMemberDraft] = useState({
    id: "",
    title: "",
    firstName: "",
    lastName: "",
    birthdate: "",
  });
  const [memberDraftErrors, setMemberDraftErrors] = useState({});
  const [memberEditorError, setMemberEditorError] = useState("");
  const [memberEditorSuccess, setMemberEditorSuccess] = useState("");

  // Add-member dialog state
  const [addMemberOpen, setAddMemberOpen] = useState(false);
  const [addMemberStep, setAddMemberStep] = useState(0);
  const [addMemberDraft, setAddMemberDraft] = useState({
    title: "",
    firstName: "",
    lastName: "",
    birthdate: "",
  });
  const [selectedOptionIndex, setSelectedOptionIndex] = useState(0);
  const [docConsents, setDocConsents] = useState({});
  const [typedName, setTypedName] = useState("");
  const [signatureChecked, setSignatureChecked] = useState(false);
  const [addMemberDraftErrors, setAddMemberDraftErrors] = useState({});
  const [addMemberError, setAddMemberError] = useState("");

  // Load site pricing/documents only when the add-member dialog is open
  const siteId = member?.standortRef?._id;
  const { data: siteInfo } = useGetMemberSitePricingInfoQuery(siteId, {
    skip: !addMemberOpen || !siteId,
  });
  const siteData = siteInfo?.site;
  const publicOptions = siteData?.publicRegistrationOptions || [];
  const siteDocuments = siteData?.publicRegistrationDocuments || [];

  const { handleSubmit, control, reset } = useForm({
    defaultValues: {
      title: "",
      firstName: "",
      lastName: "",
      birthdate: null,
      street: "",
      zip: "",
      city: "",
      email: "",
      phoneNumber: "",
      mobile: "",
    },
  });

  useEffect(() => {
    if (!member) return;
    const contract = member?.contractRef || {};
    reset({
      title: member?.title || "",
      firstName: member?.firstName || "",
      lastName: member?.lastName || "",
      birthdate: member?.birthdate || null,
      street: contract?.street || "",
      zip: contract?.zip || "",
      city: contract?.city || "",
      email: contract?.email || "",
      phoneNumber: contract?.phoneNumber || "",
      mobile: contract?.mobile || "",
    });
  }, [member, reset]);

  const onSubmit = async (data) => {
    await updateMemberSelf({
      member: {
        title: data.title,
        firstName: data.firstName,
        lastName: data.lastName,
        birthdate: getDateFromDateField(data.birthdate),
      },
      contract: {
        street: data.street,
        zip: data.zip,
        city: data.city,
        email: data.email,
        phoneNumber: data.phoneNumber,
        mobile: data.mobile,
      },
    });
  };

  const formatMonths = (value) => {
    if (value === null || value === undefined || value === "") return null;
    const n = Number(value);
    if (!Number.isFinite(n)) return String(value);
    return `${n} ${n === 1 ? "Monat" : "Monate"}`;
  };

  const degreeValue = Number(member?.degree);
  const hasValidDegree =
    Number.isInteger(degreeValue) &&
    degreeValue >= 0 &&
    degreeValue < gradArray.length;
  const degreeLabel = hasValidDegree ? gradArray[degreeValue]?.label : "—";

  const contractMembers = useMemo(() => {
    const members = member?.contractRef?.hasMembers;
    if (!Array.isArray(members)) return [];
    return members.filter(Boolean);
  }, [member]);
  const canEditContractMembers =
    Boolean(member?.contractRef?._id) && !member?.isChild;

  const memberDetails = useMemo(
    () => [
      { label: "Mitgliedsnummer", value: member?.memberNumber },
      { label: "Status", value: <StatusChip status={member?.status} /> },
      { label: "Mitgliedstyp", value: member?.memberType },
      {
        label: "Kup / Dan",
        value: hasValidDegree ? (
          <Stack direction="row" spacing={0.75} alignItems="center">
            <BeltAvatar
              beltNumber={degreeValue}
              size="small"
              onlyBelt
              justify="flex-start"
            />
            <Typography variant="body2" fontWeight={500}>
              {degreeLabel}
            </Typography>
          </Stack>
        ) : (
          "—"
        ),
      },
      { label: "Standort", value: member?.standortRef?.siteName },
      { label: "Beitritt", value: member?.beginningDate },
      { label: "Vertragsdatum", value: member?.contractDate },
      { label: "Beitrag (EUR)", value: member?.contribution },
      { label: "Anmeldegebühr (EUR)", value: member?.registrationFee },
      { label: "Bezahlt bis", value: member?.payedTill },
      { label: "Laufzeit", value: formatMonths(member?.contractDuration) },
      { label: "Kündigungsfrist", value: formatMonths(member?.noticePeriod) },
      { label: "Verlängerung", value: formatMonths(member?.contractExtension) },
      { label: "Letzte Prüfung", value: member?.lastTesting },
      { label: "Prüfungen (Anzahl)", value: member?.testings?.length },
      { label: "Geburtstag", value: member?.birthdate },
      { label: "Kündigungsdatum", value: member?.terminationDate },
      { label: "Kommentar", value: member?.comment },
    ],
    [member, degreeValue, degreeLabel, hasValidDegree],
  );

  const contractDetails = useMemo(() => {
    const c = member?.contractRef || {};
    return [
      { label: "Anrede", value: c?.title },
      { label: "Vorname", value: c?.firstName },
      { label: "Nachname", value: c?.lastName },
      { label: "Vertragspartner", value: c?.owner },
      {
        label: "Eigentümer",
        value: c?.isOwner ? "Ja" : c?.isOwner === false ? "Nein" : null,
      },
      { label: "E-Mail", value: c?.email },
      { label: "Telefon", value: c?.phoneNumber },
      { label: "Mobil", value: c?.mobile },
      { label: "Straße", value: c?.street },
      { label: "PLZ", value: c?.zip },
      { label: "Ort", value: c?.city },
      { label: "IBAN", value: c?.iban },
      { label: "BIC", value: c?.bic },
      { label: "Bank", value: c?.bank },
    ];
  }, [member]);

  const supportContactMail =
    member?.supportContactMail || member?.standortRef?.mail || "";

  useEffect(() => {
    if (!memberEditorSuccess) return undefined;
    const timer = setTimeout(() => {
      setMemberEditorSuccess("");
    }, 2500);
    return () => clearTimeout(timer);
  }, [memberEditorSuccess]);

  useEffect(() => {
    if (!contractActionFeedback?.message) return undefined;
    const timer = setTimeout(() => {
      setContractActionFeedback(null);
    }, 4000);
    return () => clearTimeout(timer);
  }, [contractActionFeedback]);

  const isPreviewActive = useReduxSelector(selectIsPreviewActive);

  if (!member && (isLoading || isFetching)) return <Loading />;

  // Show friendly hint when previewing as Mitglied without actual member data
  if (isPreviewActive && isError && !member) {
    return (
      <Container maxWidth="sm" sx={{ mt: 6 }}>
        <Helmet>
          <title>MA | Meine Daten</title>
        </Helmet>
        <Alert severity="info" variant="outlined" sx={{ py: 2 }}>
          <Typography variant="subtitle1" gutterBottom fontWeight="bold">
            Keine Mitgliedsdaten vorhanden
          </Typography>
          <Typography variant="body2">
            Ihr Benutzerkonto ist nicht als Mitglied registriert. In der
            Rollen-Vorschau können Mitgliedsseiten daher nicht mit echten Daten
            angezeigt werden. Diese Ansicht würde für ein echtes Mitglied die
            persönlichen Daten und Vertragsdetails zeigen.
          </Typography>
        </Alert>
      </Container>
    );
  }

  const handleOpenMemberEditor = (targetMember) => {
    setMemberEditorError("");
    setMemberEditorSuccess("");
    setMemberDraftErrors({});
    setMemberDraft({
      id: targetMember?._id || "",
      title: targetMember?.title || "",
      firstName: targetMember?.firstName || "",
      lastName: targetMember?.lastName || "",
      birthdate: formatDateToInput(targetMember?.birthdate),
    });
    setMemberEditorOpen(true);
  };

  const handleCloseMemberEditor = () => {
    if (updateState.isLoading) return;
    setMemberEditorOpen(false);
    setMemberDraftErrors({});
    setMemberEditorError("");
  };

  const handleMemberDraftChange = (field, value) => {
    setMemberDraft((prev) => ({ ...prev, [field]: value }));
    setMemberDraftErrors((prev) => ({ ...prev, [field]: "" }));
    setMemberEditorError("");
    setMemberEditorSuccess("");
  };

  const handleSaveContractMember = async () => {
    const nextErrors = {};
    if (!memberDraft.title) nextErrors.title = "Bitte Anrede auswählen.";
    if (!String(memberDraft.firstName || "").trim()) {
      nextErrors.firstName = "Vorname ist erforderlich.";
    }
    if (!String(memberDraft.lastName || "").trim()) {
      nextErrors.lastName = "Nachname ist erforderlich.";
    }

    if (Object.keys(nextErrors).length > 0) {
      setMemberDraftErrors(nextErrors);
      return;
    }

    try {
      await updateMemberSelf({
        contractMembers: [
          {
            id: memberDraft.id,
            title: memberDraft.title,
            firstName: memberDraft.firstName.trim(),
            lastName: memberDraft.lastName.trim(),
            birthdate: memberDraft.birthdate || null,
          },
        ],
      }).unwrap();
      setMemberEditorSuccess("Mitglied erfolgreich aktualisiert.");
      setMemberEditorOpen(false);
    } catch (saveError) {
      setMemberEditorError(
        saveError?.data?.message || "Mitglied konnte nicht gespeichert werden.",
      );
    }
  };

  const handleOpenAddMember = () => {
    setAddMemberError("");
    setAddMemberDraftErrors({});
    setAddMemberDraft({
      title: "",
      firstName: "",
      lastName: "",
      birthdate: "",
    });
    setSelectedOptionIndex(0);
    setDocConsents({});
    setTypedName("");
    setSignatureChecked(false);
    setAddMemberStep(0);
    setAddMemberOpen(true);
  };

  const handleCloseAddMember = () => {
    if (addMemberState.isLoading) return;
    setAddMemberOpen(false);
  };

  const handleAddMemberNext = () => {
    if (addMemberStep === 0) {
      const errs = {};
      if (!addMemberDraft.title) errs.title = "Bitte Anrede auswählen.";
      if (!String(addMemberDraft.firstName || "").trim())
        errs.firstName = "Vorname ist erforderlich.";
      if (!String(addMemberDraft.lastName || "").trim())
        errs.lastName = "Nachname ist erforderlich.";
      if (Object.keys(errs).length > 0) {
        setAddMemberDraftErrors(errs);
        return;
      }
    }
    setAddMemberError("");
    setAddMemberStep((s) => s + 1);
  };

  const handleAddMemberSubmit = async () => {
    const allDocConsented =
      siteDocuments.length === 0 ||
      siteDocuments.every((doc) => docConsents[doc.docId]);
    const trimmedName = String(typedName || "").trim();

    if (!allDocConsented) {
      setAddMemberError("Bitte alle Dokumente lesen und akzeptieren.");
      return;
    }
    if (!trimmedName || !signatureChecked) {
      setAddMemberError(
        "Bitte vollständigen Namen eingeben und Bestätigung abhaken.",
      );
      return;
    }

    try {
      const selectedOption =
        publicOptions.length > 0
          ? publicOptions[selectedOptionIndex]
          : {
              publicContribution: siteData?.memberContribution,
              publicContractDuration: siteData?.contractDuration,
            };

      await addMemberToContractSelf({
        member: {
          title: addMemberDraft.title,
          firstName: addMemberDraft.firstName.trim(),
          lastName: addMemberDraft.lastName.trim(),
          birthdate: addMemberDraft.birthdate || null,
        },
        publicOption: selectedOption,
        consents: siteDocuments.map((doc) => ({
          docId: doc.docId,
          accepted: Boolean(docConsents[doc.docId]),
        })),
        signature: {
          typedName: trimmedName,
          signatureCheckbox: signatureChecked,
        },
      }).unwrap();

      const fullName =
        `${addMemberDraft.firstName} ${addMemberDraft.lastName}`.trim();
      setAddMemberOpen(false);
      setContractActionFeedback({
        severity: "success",
        message: `${fullName} wurde erfolgreich zum Vertrag hinzugefügt.`,
      });
    } catch (err) {
      setAddMemberError(
        err?.data?.message || "Mitglied konnte nicht hinzugefügt werden.",
      );
    }
  };

  return (
    <>
      <Helmet>
        <title>Übersicht | Mitgliederverwaltung</title>
      </Helmet>

      <Container>
        <Stack spacing={2.5}>
          {/* ── Profil-Kopf ── */}
          <Paper
            variant="outlined"
            sx={{
              borderRadius: 3,
              borderColor: "primary.main",
              borderWidth: 1.5,
              overflow: "hidden",
              background: (t) =>
                `linear-gradient(135deg, ${alpha(
                  t.palette.primary.main,
                  0.1,
                )} 0%, ${alpha(t.palette.background.paper, 0.97)} 65%)`,
            }}
          >
            {/* Subtle refresh indicator */}
            {isFetching && (
              <LinearProgress
                sx={{
                  height: 2,
                  borderRadius: 0,
                  "& .MuiLinearProgress-bar": { borderRadius: 0 },
                }}
              />
            )}
            <Box sx={{ p: { xs: 2.5, md: 3 } }}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={{ xs: 2, sm: 3 }}
                alignItems={{ xs: "flex-start", sm: "center" }}
              >
                {hasValidDegree && (
                  <Box sx={{ flexShrink: 0 }}>
                    <BeltAvatar
                      beltNumber={degreeValue}
                      size="large"
                      onlyBelt
                      justify="flex-start"
                    />
                  </Box>
                )}

                <Box flex={1} minWidth={0}>
                  <Typography
                    variant="h5"
                    fontWeight={700}
                    sx={{ lineHeight: 1.2 }}
                  >
                    {member?.firstName} {member?.lastName}
                  </Typography>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    flexWrap="wrap"
                    sx={{ mt: 0.5 }}
                  >
                    {member?.memberNumber && (
                      <Typography variant="body2" color="text.secondary">
                        Nr.&nbsp;{member.memberNumber}
                      </Typography>
                    )}
                    {member?.standortRef?.siteName && (
                      <>
                        <Typography variant="body2" color="text.secondary">
                          ·
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {member.standortRef.siteName}
                        </Typography>
                      </>
                    )}
                    {degreeLabel && degreeLabel !== "—" && (
                      <>
                        <Typography variant="body2" color="text.secondary">
                          ·
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {degreeLabel}
                        </Typography>
                      </>
                    )}
                  </Stack>
                </Box>

                <Box sx={{ flexShrink: 0 }}>
                  <StatusChip status={member?.status} />
                </Box>
              </Stack>
            </Box>
          </Paper>

          {/* ── Persönliche Daten (editierbar) ── */}
          <SectionAccordion
            icon="mdi:account-edit-outline"
            title="Meine Daten bearbeiten"
            defaultExpanded={false}
          >
            {/* Context hint */}
            <Box
              sx={{
                mb: 2.5,
                p: 1.75,
                borderRadius: 2,
                bgcolor: (t) => alpha(t.palette.info.main, 0.07),
                border: "1px solid",
                borderColor: (t) => alpha(t.palette.info.main, 0.2),
              }}
            >
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <Iconify
                  icon="mdi:pencil-circle-outline"
                  width={18}
                  sx={{ color: "info.main", mt: 0.2, flexShrink: 0 }}
                />
                <Typography variant="body2" color="text.secondary">
                  Hier können Sie Ihre persönlichen Angaben, Kontaktdaten und
                  Adresse selbst anpassen. Klicken Sie am Ende auf{" "}
                  <strong>„Änderungen speichern"</strong>, um Ihre Eingaben zu
                  übernehmen.
                </Typography>
              </Stack>
            </Box>

            {isError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error?.data?.message || "Fehler beim Laden der Daten."}
              </Alert>
            )}
            {updateState.isSuccess && (
              <Alert
                severity="success"
                icon={<Iconify icon="mdi:check-circle-outline" width={20} />}
                sx={{ mb: 2 }}
              >
                Ihre Daten wurden erfolgreich gespeichert.
              </Alert>
            )}
            {updateState.isError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {updateState.error?.data?.message ||
                  "Speichern fehlgeschlagen."}
              </Alert>
            )}

            <form onSubmit={handleSubmit(onSubmit)}>
              <Stack spacing={3}>
                {/* Sub-section: Persönliche Angaben */}
                <FormSubSection
                  icon="mdi:account-outline"
                  title="Persönliche Angaben"
                >
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <SelectField
                        control={control}
                        name="title"
                        label="Anrede"
                        variant="outlined"
                        required
                        helperText="Wie möchten Sie angesprochen werden?"
                        itemArray={anredeArray}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        control={control}
                        name="firstName"
                        label="Vorname"
                        required
                        fullWidth
                        helperText="Ihr Rufvorname"
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        control={control}
                        name="lastName"
                        label="Nachname"
                        required
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <DEDateField
                        control={control}
                        name="birthdate"
                        label="Geburtstag"
                      />
                    </Grid>
                  </Grid>
                </FormSubSection>

                <Divider sx={{ borderStyle: "dashed" }} />

                {/* Sub-section: Kontaktdaten */}
                <FormSubSection icon="mdi:phone-outline" title="Kontaktdaten">
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        control={control}
                        name="email"
                        label="E-Mail-Adresse"
                        type="email"
                        fullWidth
                        helperText="Für Nachrichten und Benachrichtigungen"
                        rules={{
                          pattern: {
                            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                            message: "Ungültiges E-Mail-Format.",
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        control={control}
                        name="phoneNumber"
                        label="Telefon (Festnetz)"
                        fullWidth
                        helperText="z. B. 030 1234567"
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        control={control}
                        name="mobile"
                        label="Mobilnummer"
                        fullWidth
                        helperText="z. B. 0170 1234567"
                      />
                    </Grid>
                  </Grid>
                </FormSubSection>

                <Divider sx={{ borderStyle: "dashed" }} />

                {/* Sub-section: Adresse */}
                <FormSubSection icon="mdi:map-marker-outline" title="Adresse">
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={5}>
                      <TextField
                        control={control}
                        name="street"
                        label="Straße und Hausnummer"
                        fullWidth
                        helperText="z. B. Musterstraße 42"
                      />
                    </Grid>
                    <Grid item xs={6} sm={2}>
                      <TextField
                        control={control}
                        name="zip"
                        label="PLZ"
                        fullWidth
                        helperText="5-stellig"
                      />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <TextField
                        control={control}
                        name="city"
                        label="Wohnort"
                        fullWidth
                      />
                    </Grid>
                  </Grid>
                </FormSubSection>

                <Box>
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={updateState.isLoading}
                    startIcon={
                      <Iconify
                        icon={
                          updateState.isLoading
                            ? "mdi:loading"
                            : "mdi:content-save-outline"
                        }
                        width={20}
                      />
                    }
                    sx={{
                      minWidth: 200,
                      fontWeight: 700,
                      borderRadius: 2,
                      py: 1.25,
                      boxShadow: (t) =>
                        `0 4px 14px ${alpha(t.palette.primary.main, 0.3)}`,
                    }}
                  >
                    {updateState.isLoading
                      ? "Wird gespeichert …"
                      : "Änderungen speichern"}
                  </Button>
                </Box>
              </Stack>
            </form>
          </SectionAccordion>

          {/* ── Mitgliedsdaten (read-only) ── */}
          <SectionAccordion
            icon="mdi:card-account-details-outline"
            title="Mitgliedsdaten"
            badge={<ReadOnlyBadge />}
          >
            <InfoHint icon="mdi:shield-lock-outline">
              Diese Angaben werden vom Administrator verwaltet und können von
              Ihnen nicht geändert werden.
              {supportContactMail ? (
                <>
                  {" "}
                  Für Korrekturen wenden Sie sich bitte an{" "}
                  <Typography
                    component="a"
                    href={`mailto:${supportContactMail}`}
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: "primary.main",
                      textDecoration: "none",
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    {supportContactMail}
                  </Typography>
                </>
              ) : (
                " Bitte wenden Sie sich an Ihren Administrator."
              )}
            </InfoHint>

            <Grid container spacing={2}>
              {memberDetails.map((item) => (
                <KeyValueRow
                  key={item.label}
                  label={item.label}
                  value={item.value}
                />
              ))}
            </Grid>
          </SectionAccordion>

          {/* ── Mitglieder im Vertrag ── */}
          {(contractMembers.length > 0 || canEditContractMembers) && (
            <SectionAccordion
              icon="mdi:account-group-outline"
              title={`Mitglieder im Vertrag (${contractMembers.length})`}
              action={
                canEditContractMembers ? (
                  <Button
                    startIcon={<Iconify icon="mdi:account-plus" />}
                    onClick={handleOpenAddMember}
                    variant="outlined"
                    size="small"
                  >
                    Mitglied hinzufügen
                  </Button>
                ) : null
              }
            >
              <Collapse
                in={Boolean(contractActionFeedback?.message)}
                timeout={200}
              >
                <Alert
                  severity={contractActionFeedback?.severity || "success"}
                  sx={{ mb: 2 }}
                >
                  {contractActionFeedback?.message}
                </Alert>
              </Collapse>
              <Collapse in={Boolean(memberEditorSuccess)} timeout={200}>
                <Alert severity="success" sx={{ mb: 2 }}>
                  {memberEditorSuccess}
                </Alert>
              </Collapse>

              {!canEditContractMembers && (
                <InfoHint icon="mdi:account-lock-outline">
                  Änderungen an Grunddaten der Vertragsmitglieder sind nur für
                  den Vertragsnehmer möglich.
                </InfoHint>
              )}

              {contractMembers.length === 0 && canEditContractMembers && (
                <InfoHint icon="mdi:account-plus-outline">
                  Noch keine weiteren Mitglieder im Vertrag. Klicken Sie auf
                  „Mitglied hinzufügen", um ein neues Mitglied anzulegen.
                </InfoHint>
              )}

              <Stack spacing={1.5} sx={{ pt: 0.5 }}>
                {contractMembers.map((m) => {
                  const mDeg = Number(m.degree);
                  const mHasValidDeg =
                    Number.isInteger(mDeg) &&
                    mDeg >= 0 &&
                    mDeg < gradArray.length;
                  const mDegLabel = mHasValidDeg ? gradArray[mDeg]?.label : "—";
                  const isTerminated = m.status === "terminated";

                  return (
                    <Paper
                      key={m._id}
                      variant="outlined"
                      sx={{
                        borderRadius: 2,
                        overflow: "hidden",
                        borderColor: "divider",
                        transition:
                          "border-color 0.16s ease-out, transform 0.16s ease-out, box-shadow 0.16s ease-out, opacity 0.2s, filter 0.2s",
                        transform: "translateY(0)",
                        boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
                        opacity: isTerminated ? 0.72 : 1,
                        filter: isTerminated ? "grayscale(0.35)" : "none",
                        "&:hover": {
                          borderColor: isTerminated
                            ? "divider"
                            : "primary.light",
                          boxShadow: "0 8px 18px rgba(16, 24, 40, 0.1)",
                          ...(canEditContractMembers &&
                            !isTerminated && {
                              transform: "translateY(-2px)",
                            }),
                        },
                      }}
                    >
                      {/* Mitglieder-Kopf */}
                      <Stack
                        direction="row"
                        spacing={1.5}
                        alignItems="center"
                        sx={{
                          px: 2,
                          py: 1.5,
                          background: (t) =>
                            isTerminated
                              ? alpha(t.palette.grey[400], 0.3)
                              : alpha(t.palette.primary.main, 0.07),
                        }}
                      >
                        {mHasValidDeg && (
                          <BeltAvatar
                            beltNumber={mDeg}
                            size="small"
                            onlyBelt
                            justify="flex-start"
                          />
                        )}
                        <Box flex={1} minWidth={0}>
                          <Typography variant="body1" fontWeight={600} noWrap>
                            {m.firstName} {m.lastName}
                          </Typography>
                          {m.memberNumber && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Nr.&nbsp;{m.memberNumber}
                            </Typography>
                          )}
                        </Box>
                        <StatusChip status={m.status} />
                      </Stack>

                      {/* Mitglieder-Details */}
                      <Box sx={{ px: 2, py: 1.5 }}>
                        <Grid container spacing={1.5}>
                          <Grid item xs={6} sm={4}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              display="block"
                              sx={{ mb: 0.25 }}
                            >
                              Geburtstag
                            </Typography>
                            <Typography variant="body2" fontWeight={500}>
                              {renderValue(m.birthdate)}
                            </Typography>
                          </Grid>
                          <Grid item xs={6} sm={4}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              display="block"
                              sx={{ mb: 0.25 }}
                            >
                              Gürtel / Grad
                            </Typography>
                            {mHasValidDeg ? (
                              <Stack
                                direction="row"
                                spacing={0.75}
                                alignItems="center"
                              >
                                <BeltAvatar
                                  beltNumber={mDeg}
                                  size="small"
                                  onlyBelt
                                  justify="flex-start"
                                />
                                <Typography variant="body2" fontWeight={500}>
                                  {mDegLabel}
                                </Typography>
                              </Stack>
                            ) : (
                              <Typography variant="body2" fontWeight={500}>
                                —
                              </Typography>
                            )}
                          </Grid>
                        </Grid>

                        {canEditContractMembers && (
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            sx={{ mt: 2 }}
                          >
                            {/* Kündigen / Reaktivieren */}
                            {!isTerminated ? (
                              <Tooltip
                                title="Mitgliedschaft kündigen"
                                placement="top"
                                arrow
                              >
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="error"
                                  startIcon={
                                    <Iconify
                                      icon="mdi:file-document-remove-outline"
                                      width={16}
                                    />
                                  }
                                  onClick={() =>
                                    setTerminateTarget({
                                      member: m,
                                      mode: "terminate",
                                    })
                                  }
                                  sx={{
                                    opacity: 0.55,
                                    transition: "opacity 0.2s ease",
                                    ".MuiPaper-root:hover &": { opacity: 1 },
                                  }}
                                >
                                  Kündigen
                                </Button>
                              </Tooltip>
                            ) : (
                              <Tooltip
                                title="Mitgliedschaft reaktivieren"
                                placement="top"
                                arrow
                              >
                                <Button
                                  size="small"
                                  color="success"
                                  variant="outlined"
                                  onClick={() =>
                                    setTerminateTarget({
                                      member: m,
                                      mode: "reactivate",
                                    })
                                  }
                                  startIcon={
                                    <Iconify
                                      icon="mdi:account-reactivate-outline"
                                      width={16}
                                    />
                                  }
                                >
                                  Reaktivieren
                                </Button>
                              </Tooltip>
                            )}

                            {/* Grunddaten bearbeiten — nur wenn nicht gekündigt */}
                            {!isTerminated && (
                              <Button
                                size="small"
                                variant="contained"
                                color="primary"
                                startIcon={
                                  <Iconify icon="mdi:account-edit-outline" />
                                }
                                onClick={() => handleOpenMemberEditor(m)}
                              >
                                Grunddaten bearbeiten
                              </Button>
                            )}
                          </Stack>
                        )}
                      </Box>
                    </Paper>
                  );
                })}
              </Stack>
            </SectionAccordion>
          )}

          {/* ── Vertragsdaten (read-only) ── */}
          <SectionAccordion
            icon="mdi:file-document-outline"
            title="Vertragsdaten"
            badge={<ReadOnlyBadge />}
          >
            <InfoHint icon="mdi:information-outline">
              Die Vertragsdaten werden automatisch aus Ihrem Vertrag übernommen
              und können nur durch den Administrator geändert werden.
            </InfoHint>
            <Grid container spacing={2} sx={{ pt: 0.5 }}>
              {contractDetails.map((item) => (
                <KeyValueRow
                  key={item.label}
                  label={item.label}
                  value={item.value}
                />
              ))}
            </Grid>
          </SectionAccordion>
        </Stack>
      </Container>

      {terminateTarget?.member && (
        <SelfTerminateMemberDialog
          member={terminateTarget.member}
          primaryMember={member}
          mode={terminateTarget.mode}
          onClose={() => setTerminateTarget(null)}
          onSuccess={(payload) => {
            const targetName = `${terminateTarget?.member?.firstName || ""} ${
              terminateTarget?.member?.lastName || ""
            }`.trim();
            if (payload?.action === "reactivate") {
              setContractActionFeedback({
                severity: "success",
                message: `${targetName} wurde reaktiviert. Bezahlt bis: ${
                  payload?.payedTill || "aktueller Monat"
                }.`,
              });
            } else {
              setContractActionFeedback({
                severity: "success",
                message: `${targetName} wurde gekündigt.${
                  payload?.lastMonth
                    ? ` Letzter Beitragsmonat: ${payload.lastMonth}.`
                    : ""
                }`,
              });
            }
            setTerminateTarget(null);
          }}
        />
      )}

      <Dialog
        open={memberEditorOpen}
        onClose={handleCloseMemberEditor}
        fullWidth
        maxWidth="sm"
        TransitionComponent={MemberDialogTransition}
        transitionDuration={{ enter: 220, exit: 160 }}
      >
        <DialogTitle
          sx={{
            pb: 1.25,
            background: (t) =>
              `linear-gradient(120deg, ${alpha(
                t.palette.primary.main,
                0.14,
              )} 0%, transparent 70%)`,
          }}
        >
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Iconify icon="mdi:card-account-details-star-outline" width={20} />
            <Typography variant="h6" fontWeight={700}>
              Mitglied bearbeiten
            </Typography>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ mt: 2.5 }}>
          {memberEditorError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {memberEditorError}
            </Alert>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
            Bitte geben Sie die Grunddaten des Mitglieds ein. Mit{" "}
            <strong>*</strong> markierte Felder sind Pflichtfelder.
          </Typography>
          <Grid container spacing={1.75}>
            <Grid item xs={12} sm={4}>
              <MuiTextField
                sx={{ mt: 1 }}
                select
                fullWidth
                required
                label="Anrede"
                value={memberDraft.title}
                error={Boolean(memberDraftErrors.title)}
                helperText={memberDraftErrors.title || "Pflichtfeld"}
                onChange={(event) =>
                  handleMemberDraftChange("title", event.target.value)
                }
              >
                {anredeArray.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </MuiTextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <MuiTextField
                fullWidth
                required
                label="Vorname"
                value={memberDraft.firstName}
                error={Boolean(memberDraftErrors.firstName)}
                helperText={memberDraftErrors.firstName || "Pflichtfeld"}
                onChange={(event) =>
                  handleMemberDraftChange("firstName", event.target.value)
                }
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <MuiTextField
                fullWidth
                required
                label="Nachname"
                value={memberDraft.lastName}
                error={Boolean(memberDraftErrors.lastName)}
                helperText={memberDraftErrors.lastName || "Pflichtfeld"}
                onChange={(event) =>
                  handleMemberDraftChange("lastName", event.target.value)
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <MuiTextField
                fullWidth
                label="Geburtstag"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={memberDraft.birthdate}
                helperText="Format: TT.MM.JJJJ"
                onChange={(event) =>
                  handleMemberDraftChange("birthdate", event.target.value)
                }
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.25 }}>
          <Button
            onClick={handleCloseMemberEditor}
            disabled={updateState.isLoading}
          >
            Abbrechen
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveContractMember}
            disabled={updateState.isLoading}
            startIcon={<Iconify icon="mdi:content-save-outline" />}
          >
            {updateState.isLoading ? "Wird gespeichert …" : "Speichern"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Mitglied hinzufügen Dialog ── */}
      <Dialog
        open={addMemberOpen}
        onClose={handleCloseAddMember}
        fullWidth
        maxWidth="sm"
        TransitionComponent={MemberDialogTransition}
        transitionDuration={{ enter: 220, exit: 160 }}
      >
        <DialogTitle
          sx={{
            pb: 1.25,
            background: (t) =>
              `linear-gradient(120deg, ${alpha(
                t.palette.primary.main,
                0.14,
              )} 0%, transparent 70%)`,
          }}
        >
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Iconify icon="mdi:account-plus" width={20} />
            <Typography variant="h6" fontWeight={700}>
              Mitglied hinzufügen
            </Typography>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ pt: 2.5 }}>
          <Stepper activeStep={addMemberStep} sx={{ mb: 3 }}>
            <Step>
              <StepLabel>Stammdaten</StepLabel>
            </Step>
            <Step>
              <StepLabel>Konditionen</StepLabel>
            </Step>
            <Step>
              <StepLabel>Einwilligung</StepLabel>
            </Step>
          </Stepper>

          {addMemberError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {addMemberError}
            </Alert>
          )}

          {/* ── Schritt 1: Stammdaten ── */}
          {addMemberStep === 0 && (
            <Grid container spacing={1.75}>
              <Grid item xs={12} sm={4}>
                <MuiTextField
                  sx={{ mt: 1 }}
                  select
                  fullWidth
                  required
                  label="Anrede"
                  value={addMemberDraft.title}
                  error={Boolean(addMemberDraftErrors.title)}
                  helperText={addMemberDraftErrors.title || "Pflichtfeld"}
                  onChange={(e) => {
                    setAddMemberDraft((p) => ({ ...p, title: e.target.value }));
                    setAddMemberDraftErrors((p) => ({ ...p, title: "" }));
                  }}
                >
                  {anredeArray.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </MuiTextField>
              </Grid>
              <Grid item xs={12} sm={4}>
                <MuiTextField
                  fullWidth
                  required
                  label="Vorname"
                  value={addMemberDraft.firstName}
                  error={Boolean(addMemberDraftErrors.firstName)}
                  helperText={addMemberDraftErrors.firstName || "Pflichtfeld"}
                  onChange={(e) => {
                    setAddMemberDraft((p) => ({
                      ...p,
                      firstName: e.target.value,
                    }));
                    setAddMemberDraftErrors((p) => ({ ...p, firstName: "" }));
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <MuiTextField
                  fullWidth
                  required
                  label="Nachname"
                  value={addMemberDraft.lastName}
                  error={Boolean(addMemberDraftErrors.lastName)}
                  helperText={addMemberDraftErrors.lastName || "Pflichtfeld"}
                  onChange={(e) => {
                    setAddMemberDraft((p) => ({
                      ...p,
                      lastName: e.target.value,
                    }));
                    setAddMemberDraftErrors((p) => ({ ...p, lastName: "" }));
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <MuiTextField
                  fullWidth
                  label="Geburtsdatum"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={addMemberDraft.birthdate}
                  helperText="Empfohlen – Format: TT.MM.JJJJ"
                  onChange={(e) =>
                    setAddMemberDraft((p) => ({
                      ...p,
                      birthdate: e.target.value,
                    }))
                  }
                />
              </Grid>
            </Grid>
          )}

          {/* ── Schritt 2: Beitragskonditionen ── */}
          {addMemberStep === 1 && (
            <Box>
              <Alert severity="info" sx={{ mb: 2.5 }}>
                Die Konditionen gelten für das neue Mitglied unter Ihrem
                bestehenden Vertrag.
              </Alert>

              {publicOptions.length > 1 ? (
                <Box>
                  <Typography
                    variant="subtitle2"
                    fontWeight={600}
                    sx={{ mb: 1 }}
                  >
                    Monatlicher Beitrag und Mindestvertragslaufzeit wählen
                  </Typography>
                  <RadioGroup
                    value={String(selectedOptionIndex)}
                    onChange={(e) =>
                      setSelectedOptionIndex(Number(e.target.value))
                    }
                  >
                    <Stack spacing={1}>
                      {publicOptions.map((opt, i) => (
                        <Paper
                          key={i}
                          variant="outlined"
                          sx={{
                            p: 1.5,
                            borderRadius: 1.5,
                            borderColor:
                              selectedOptionIndex === i
                                ? "primary.main"
                                : "divider",
                            bgcolor:
                              selectedOptionIndex === i
                                ? (t) => alpha(t.palette.primary.main, 0.05)
                                : "background.paper",
                            transition:
                              "border-color 0.15s, background-color 0.15s",
                          }}
                        >
                          <FormControlLabel
                            value={String(i)}
                            control={<Radio size="small" />}
                            label={
                              <Typography variant="body2">
                                <strong>
                                  {Number(opt.publicContribution).toFixed(2)} €
                                </strong>
                                {" / "}
                                {opt.publicContractDuration}{" "}
                                {opt.publicContractDuration === 1
                                  ? "Monat"
                                  : "Monate"}
                                {opt.label ? ` – ${opt.label}` : ""}
                              </Typography>
                            }
                            sx={{ m: 0 }}
                          />
                        </Paper>
                      ))}
                    </Stack>
                  </RadioGroup>
                </Box>
              ) : (
                <Stack spacing={1.5}>
                  {[
                    {
                      label: "Monatlicher Beitrag",
                      value:
                        publicOptions.length === 1
                          ? `${Number(
                              publicOptions[0].publicContribution,
                            ).toFixed(2)} €`
                          : siteData?.memberContribution != null
                          ? `${Number(siteData.memberContribution).toFixed(
                              2,
                            )} €`
                          : "—",
                    },
                    {
                      label: "Laufzeit",
                      value:
                        publicOptions.length === 1
                          ? `${publicOptions[0].publicContractDuration} Monate`
                          : siteData?.contractDuration != null
                          ? `${siteData.contractDuration} Monate`
                          : "—",
                    },
                    {
                      label: "Kündigungsfrist",
                      value:
                        siteData?.noticePeriod != null
                          ? `${siteData.noticePeriod} Monate`
                          : "—",
                    },
                    ...(siteData?.enrollmentFee > 0
                      ? [
                          {
                            label: "Anmeldegebühr",
                            value: `${Number(siteData.enrollmentFee).toFixed(
                              2,
                            )} €`,
                          },
                        ]
                      : []),
                  ].map((row) => (
                    <Stack
                      key={row.label}
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{
                        px: 1.75,
                        py: 1,
                        borderRadius: 1.5,
                        bgcolor: (t) => alpha(t.palette.grey[500], 0.06),
                        border: "1px solid",
                        borderColor: "divider",
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        {row.label}
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {row.value}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              )}
            </Box>
          )}

          {/* ── Schritt 3: Einwilligung & Bestätigung ── */}
          {addMemberStep === 2 && (
            <Box>
              <Alert severity="info" sx={{ mb: 2.5 }}>
                Das neue Mitglied wird zu Ihrem bestehenden Vertrag hinzugefügt.
                Die für Ihren Vertrag gültigen Konditionen gelten auch für das
                neue Mitglied. Bei Fragen wenden Sie sich an Ihre/n
                Standortleiter/in oder Trainer/in.
              </Alert>

              {siteDocuments.length > 0 && (
                <>
                  <Typography
                    variant="subtitle2"
                    fontWeight={600}
                    sx={{ mb: 0.5 }}
                  >
                    Einwilligungen bestätigen
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1.5 }}
                  >
                    Bitte lesen Sie die Dokumente und setzen Sie für jede
                    Einwilligung einen Haken.
                  </Typography>
                  <Stack spacing={1} sx={{ mb: 2.5 }}>
                    {siteDocuments.map((doc) => (
                      <Paper
                        key={doc.docId}
                        variant="outlined"
                        sx={{
                          p: 1.5,
                          borderRadius: 1.5,
                          borderColor: docConsents[doc.docId]
                            ? "success.main"
                            : "divider",
                          bgcolor: (t) =>
                            docConsents[doc.docId]
                              ? alpha(t.palette.success.main, 0.04)
                              : "background.paper",
                          transition:
                            "border-color 0.15s, background-color 0.15s",
                        }}
                      >
                        {doc.storageKey && (
                          <Box sx={{ mb: 0.75 }}>
                            <Button
                              size="small"
                              variant="text"
                              href={`${serverURL()}/public/files/${
                                doc.storageKey
                              }`}
                              target="_blank"
                              rel="noopener noreferrer"
                              startIcon={
                                <Iconify
                                  icon="mdi:file-download-outline"
                                  width={16}
                                />
                              }
                            >
                              {doc.title || doc.fileName || "Dokument öffnen"}
                            </Button>
                          </Box>
                        )}
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={Boolean(docConsents[doc.docId])}
                              onChange={(e) =>
                                setDocConsents((prev) => ({
                                  ...prev,
                                  [doc.docId]: e.target.checked,
                                }))
                              }
                              color="success"
                            />
                          }
                          label={
                            <Typography variant="body2">
                              {doc.consentText || "Ich stimme dem Dokument zu."}
                            </Typography>
                          }
                          sx={{ m: 0, alignItems: "flex-start" }}
                        />
                      </Paper>
                    ))}
                  </Stack>
                  <Divider sx={{ mb: 2.5 }} />
                </>
              )}

              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
                Digitale Unterschrift
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 1.5 }}
              >
                Geben Sie Ihren vollständigen Namen ein und bestätigen Sie die
                Angaben.
              </Typography>
              <MuiTextField
                fullWidth
                label="Vollständiger Name (Unterschrift)"
                value={typedName}
                onChange={(e) => {
                  setTypedName(e.target.value);
                  setAddMemberError("");
                }}
                sx={{ mb: 1.5 }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={signatureChecked}
                    onChange={(e) => {
                      setSignatureChecked(e.target.checked);
                      setAddMemberError("");
                    }}
                    color="primary"
                  />
                }
                label={
                  <Typography variant="body2">
                    Ich bestätige, dass alle Angaben korrekt sind und stimme dem
                    Beitritt unter den genannten Konditionen zu.
                  </Typography>
                }
                sx={{ alignItems: "flex-start" }}
              />
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.25 }}>
          <Button
            onClick={handleCloseAddMember}
            disabled={addMemberState.isLoading}
          >
            Abbrechen
          </Button>
          {addMemberStep > 0 && (
            <Button
              onClick={() => {
                setAddMemberError("");
                setAddMemberStep((s) => s - 1);
              }}
              disabled={addMemberState.isLoading}
            >
              Zurück
            </Button>
          )}
          {addMemberStep < 2 ? (
            <Button
              variant="contained"
              onClick={handleAddMemberNext}
              startIcon={<Iconify icon="mdi:arrow-right" />}
            >
              Weiter
            </Button>
          ) : (
            <Button
              variant="contained"
              color="primary"
              onClick={handleAddMemberSubmit}
              disabled={addMemberState.isLoading}
              startIcon={
                <Iconify
                  icon={
                    addMemberState.isLoading
                      ? "mdi:loading"
                      : "mdi:account-plus"
                  }
                />
              }
            >
              {addMemberState.isLoading
                ? "Wird gespeichert …"
                : "Mitglied hinzufügen"}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}
